import { createHook, createStore, StoreActionApi } from "react-sweet-state"
import { MemberApplication, MemberApplicationDefaults, MemberApplicationFormState, MemberApplicationState } from "./entity"
import { LoadingStateDefaults } from "components/state/loadingstate"
import { EmptyStateProducer, load, update } from "components/state/loader"
import { Container } from "components/state/types"
import { EndpointResponse } from "components/http/endpoint"
import { NoEntityError, updateByResponse, updateByEntityResponse } from "components/validation/validatableentity"
import { EntityValidator, FieldValidator } from "components/validation/validator"
import { storeAction } from "components/state/immersetup"
import { EntityState, setEntityField, setStateField } from "components/state/entity"
import { createNew, load as loadEntity, save as saveEntity, sendEmail as sendMemoEmail, getPdfUrl } from "./endpoint"
import { setEntityField as setStateEntityField } from "components/state/setfield";

// State
enum SavePhase {
    None,
    Saving,
    Saved,
    SubmitConfirmation,
    Submitting,
    Submitted,
}

enum MemberValidationPhase {
    None,
    GetValidationState,
    EmailValidationRequestBegin,
    EmailValidationRequestEnd,
    PhoneValidationRequestBegin,
    PhoneValidationRequestEnd,
    PhoneValidationRequestSucceeded,
    AddPhoneValidationCode,
}

interface State extends EntityState<MemberApplication> {
    savePhase: SavePhase;
    memberValidationPhase: MemberValidationPhase;
    previousMemberEmail: string;
    clubID: number;
}

const StateDefaults: State = {
    ...LoadingStateDefaults,
    entity: MemberApplicationDefaults,
    errors: { ...NoEntityError },
    savePhase: SavePhase.None,
    memberValidationPhase: MemberValidationPhase.None,
    previousMemberEmail: null,
    clubID: null,
}

// Helpers
const isExisting = (state: State): boolean => {
    const id = state.entity.id;
    return id != null && id !== 0;
}

const isFormState = (state: State, formStateFlag: MemberApplicationFormState): boolean => {
    return (state.entity.formFlags & formStateFlag) !== 0;
}

const setFormStateDraft = (draft: State, formStateFlag: MemberApplicationFormState, value: boolean) => {

    if (value) {
        draft.entity.formFlags |= formStateFlag;
    } else {
        draft.entity.formFlags &= ~formStateFlag;
    }
}

// Validation
const validate = (store: StoreActionApi<State>, requireExisting: boolean): boolean => {
    let entity = store.getState().entity;
    let validator = EntityValidator();

    validator.storeFieldResult("member.email", FieldValidator()
        .validateNotEmpty(entity.member.email)
        .getResult());

    if (entity.member.ownedIdt) {
        validator.storeFieldResult("member.ownedIdt", FieldValidator()
            .validateIntGZ(entity.member.ownedIdt)
            .getResult());
    }

    store.setState((draft) => {
        draft.errors = validator.getResult()
    });

    return validator.isValid();
}

// Actions

const resetDefaults = () =>
    (store: StoreActionApi<State>) => {
        store.setState((draft) => {
            let newEmptyState = Object.assign(StateDefaults, {});
            draft = Object.assign(draft, newEmptyState);
        });
    };

const loadApplication = (id: number, hashCode: string = null, clubId: number = null) =>
    (store: StoreActionApi<State>) => {

        let loader = () => loadEntity(id, hashCode, clubId);

        let stateProducer = (draft: State, _: Container, response: EndpointResponse): void => {
            updateByEntityResponse(draft, response);
            if (response.succeeded) {
                draft.previousMemberEmail = draft.entity.member.email;
                draft.memberValidationPhase = MemberValidationPhase.GetValidationState;
            }
        }

        load<State>(store, loader, stateProducer);
    };

const setClubID = (clubID: number) =>
    (store: StoreActionApi<State>) => {
        store.setState((draft) => {
            draft.entity.clubId = clubID;
        });
    };

const save = () =>
    (store: StoreActionApi<State>) => {
        if (!validate(store, false)) {
            store.setState((draft) => {
                draft.savePhase = SavePhase.None;
            });
            return;
        }

        store.setState((draft) => {
            draft.savePhase = SavePhase.Saving;
        });

        const existing = isExisting(store.getState());
        const application = store.getState().entity;

        let updater =
            () => existing ?
                saveEntity(application) :
                createNew(application);

        let stateProducer = (draft: State, _: Container, response: EndpointResponse): void => {

            updateByEntityResponse(draft, response);

            if (response.succeeded) {
                draft.memberValidationPhase = MemberValidationPhase.GetValidationState;
            }

            draft.savePhase = response.succeeded
                ? SavePhase.Saved
                : SavePhase.None;
        }

        update<State>(store, updater, stateProducer);
    };

const accept = () =>
    (store: StoreActionApi<State>) => {
        if (!validate(store, true)) {
            store.setState((draft) => {
                draft.savePhase = SavePhase.None;
            });
            return;
        }

        store.setState((draft) => {
            draft.savePhase = SavePhase.Submitting;
        });

        const existing = isExisting(store.getState());

        const savedEntity = { ...store.getState().entity };
        savedEntity.state = MemberApplicationState.Submitted;

        let updater = () => existing
            ? saveEntity(savedEntity)
            : createNew(savedEntity);

        let stateProducer = (draft: State, _: Container, response: EndpointResponse): void => {

            updateByEntityResponse(draft, response);

            if (response.succeeded) {
                draft.savePhase = SavePhase.Submitted;
            }
            else {
                draft.savePhase = SavePhase.None;
            }
        }

        update<State>(store, updater, stateProducer);
    };

const cancel = () =>
    (store: StoreActionApi<State>) => {
        if (!validate(store, true)) {
            return;
        }

        const savedEntity = { ...store.getState().entity };
        savedEntity.state = MemberApplicationState.Cancelled;

        let updater = () => saveEntity(savedEntity);

        let stateProducer = (draft: State, _: Container, response: EndpointResponse): void => {
            updateByResponse(draft, response);
        }

        update<State>(store, updater, stateProducer);
    };

const emailReceived = () =>
    (store: StoreActionApi<State>) => {
        store.setState((draft) => {
            draft.savePhase = SavePhase.None;
            draft.previousMemberEmail = draft.entity.member.email;
        });
    };


const backToForm = () =>
    (store: StoreActionApi<State>) => {
        store.setState((draft) => {
            draft.savePhase = SavePhase.None;
        });
    };


const sendEmail = () =>
    (store: StoreActionApi<State>) => {
        if (!validate(store, true)) {
            return;
        }

        let updater = () => sendMemoEmail(store.getState().entity);

        update<State>(store, updater, EmptyStateProducer);
    };

const verifyEmail = () =>
    (store: StoreActionApi<State>) => {

        if (!validate(store, false)) {
            return;
        }

        store.setState((draft) => {
            draft.memberValidationPhase = MemberValidationPhase.EmailValidationRequestBegin;
        });


        const existing = isExisting(store.getState());

        const application = { ...store.getState().entity };
        application.formFlags |= MemberApplicationFormState.IsEmailVerification;
        let updater = () => existing
            ? saveEntity(application)
            : createNew(application);

        let stateProducer = (draft: State, _: Container, response: EndpointResponse): void => {
            updateByEntityResponse(draft, response);
            draft.memberValidationPhase = MemberValidationPhase.EmailValidationRequestEnd;
        }

        update<State>(store, updater, stateProducer);
    };

const verifyPhone = () =>
    (store: StoreActionApi<State>) => {

        if (!validate(store, false)) {
            return;
        }

        const existing = isExisting(store.getState());
        const application = { ...store.getState().entity };
        application.formFlags |= MemberApplicationFormState.IsPhoneVerification;

        let updater = () => existing
            ? saveEntity(application)
            : createNew(application);

        let stateProducer = (draft: State, _: Container, response: EndpointResponse): void => {

            updateByEntityResponse(draft, response);

            if (response.succeeded && draft.errors['member.mobilePhone'] == null) {
                draft.memberValidationPhase = MemberValidationPhase.PhoneValidationRequestSucceeded;
            } else {
                draft.memberValidationPhase = MemberValidationPhase.PhoneValidationRequestEnd;
            }
        }

        update<State>(store, updater, stateProducer);
    };


const setFormState = (formStateFlag: MemberApplicationFormState, value: boolean) =>
    storeAction<State>((draft: State) => {
        setFormStateDraft(draft, formStateFlag, value);
    });

const setEntityFieldWithGlobalValidation = (
    field: string,
    value: boolean | string | number) =>
    (store: StoreActionApi<State>) => {
        store.setState((draft) => {
            setStateEntityField(draft, field, value);
        });
        validate(store, false);
    }

const setMemberValidationPhase = (phase?: MemberValidationPhase) => (store: StoreActionApi<State>) => {
    store.setState((draft) => {
        draft.memberValidationPhase = phase || MemberValidationPhase.None;
    });
}

const store = createStore({
    initialState: StateDefaults,
    actions: {
        accept,
        cancel,
        loadApplication,
        resetDefaults,
        save,
        sendEmail,
        emailReceived,
        verifyEmail,
        verifyPhone,
        setMemberValidationPhase,
        backToForm,
        setFormState,
        setEntityField,
        setEntityFieldWithGlobalValidation,
        setStateField,
        setClubID,
    },
    name: "Application"
});

const useState = createHook(store);

export {
    isFormState,
    getPdfUrl,
    SavePhase,
    MemberValidationPhase,
    StateDefaults as ApplicationStateDefaults,
    State as ApplicationState,
    useState as useApplication,
}
